import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Add, Close } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import SavingOverlay from "../components/SavingOverlay";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import { entityService, BusinessHour } from "../services/entityService";
import { colors, typography, inputs, pageHeader } from "../theme/designSystem";

// 0=domingo … 6=sábado (mesma convenção do BusinessHour).
const WEEKDAY_LABELS = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

// Faixa de horário na UI em "HH:mm"; convertida de/para minutos ao carregar/salvar.
interface TimeRange {
  start: string;
  end: string;
}

const minutesToHHmm = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const mins = (minutes % 60).toString().padStart(2, "0");
  return `${hours}:${mins}`;
};

const hhmmToMinutes = (value: string): number => {
  const [hours, mins] = value.split(":").map(Number);
  return hours * 60 + mins;
};

const emptyDayRanges = (): TimeRange[][] => Array.from({ length: 7 }, () => []);

// Opções de horário em passos de 30 min (dropdown MUI — padrão do site, consistente em todo navegador).
const TIME_STEP_MIN = 30;
const TIME_OPTIONS: string[] = Array.from(
  { length: (24 * 60) / TIME_STEP_MIN },
  (_, i) => minutesToHHmm(i * TIME_STEP_MIN)
);

// Inclui um valor fora do passo (ex.: 08:15 herdado) para o Select não ficar vazio.
const timeOptions = (current: string): string[] =>
  current && !TIME_OPTIONS.includes(current)
    ? [...TIME_OPTIONS, current].sort()
    : TIME_OPTIONS;

const EntityBusinessHours: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const entityId = params.get("entityId") || user?.entityId || "";
  const entityName =
    params.get("entityName") || user?.entityName || "Entidade";

  const [dayRanges, setDayRanges] = useState<TimeRange[][]>(emptyDayRanges);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hoursError, setHoursError] = useState<string>("");

  useEffect(() => {
    document.title = `${entityName} / Horários de Atendimento`;
    if (!entityId) return;

    let cancelled = false;
    setLoading(true);
    entityService
      .getBusinessHours(entityId)
      .then((hours) => {
        if (cancelled) return;
        const grouped = emptyDayRanges();
        hours.forEach((h) => {
          if (h.weekday >= 0 && h.weekday <= 6) {
            grouped[h.weekday].push({
              start: minutesToHHmm(h.startMinute),
              end: minutesToHHmm(h.endMinute),
            });
          }
        });
        grouped.forEach((ranges) =>
          ranges.sort((a, b) => hhmmToMinutes(a.start) - hhmmToMinutes(b.start))
        );
        setDayRanges(grouped);
      })
      .catch(() => {
        if (!cancelled) {
          showToast(
            "Não foi possível carregar os horários de atendimento.",
            "error"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const addRange = (weekday: number) => {
    setDayRanges((prev) =>
      prev.map((ranges, i) =>
        i === weekday ? [...ranges, { start: "08:00", end: "12:00" }] : ranges
      )
    );
    if (hoursError) setHoursError("");
  };

  const removeRange = (weekday: number, index: number) => {
    setDayRanges((prev) =>
      prev.map((ranges, i) =>
        i === weekday ? ranges.filter((_, j) => j !== index) : ranges
      )
    );
    if (hoursError) setHoursError("");
  };

  const updateRange = (
    weekday: number,
    index: number,
    field: "start" | "end",
    value: string
  ) => {
    setDayRanges((prev) =>
      prev.map((ranges, i) =>
        i === weekday
          ? ranges.map((r, j) => (j === index ? { ...r, [field]: value } : r))
          : ranges
      )
    );
    if (hoursError) setHoursError("");
  };

  // Replica as faixas de segunda-feira nos demais dias úteis (terça a sexta).
  const copyMondayToWeekdays = () => {
    setDayRanges((prev) => {
      const monday = prev[1].map((r) => ({ ...r }));
      return prev.map((ranges, i) =>
        i >= 2 && i <= 5 ? monday.map((r) => ({ ...r })) : ranges
      );
    });
    if (hoursError) setHoursError("");
  };

  const copyMondayToAll = () => {
    setDayRanges((prev) => {
      const monday = prev[1];
      return prev.map(() => monday.map((r) => ({ ...r })));
    });
    if (hoursError) setHoursError("");
  };

  // Validação leve no cliente (o backend também valida): fim > início e sem sobreposição.
  const validateHours = (): string => {
    for (let d = 0; d < 7; d++) {
      const ranges = dayRanges[d];
      for (const r of ranges) {
        if (!r.start || !r.end) {
          return `${WEEKDAY_LABELS[d]}: preencha início e fim de todas as faixas.`;
        }
        if (hhmmToMinutes(r.end) <= hhmmToMinutes(r.start)) {
          return `${WEEKDAY_LABELS[d]}: o horário final deve ser maior que o inicial.`;
        }
      }
      const sorted = [...ranges].sort(
        (a, b) => hhmmToMinutes(a.start) - hhmmToMinutes(b.start)
      );
      for (let i = 1; i < sorted.length; i++) {
        if (hhmmToMinutes(sorted[i].start) < hhmmToMinutes(sorted[i - 1].end)) {
          return `${WEEKDAY_LABELS[d]}: as faixas de horário não podem se sobrepor.`;
        }
      }
    }
    return "";
  };

  const flattenHours = (): BusinessHour[] => {
    const result: BusinessHour[] = [];
    dayRanges.forEach((ranges, weekday) => {
      ranges.forEach((r) => {
        result.push({
          weekday,
          startMinute: hhmmToMinutes(r.start),
          endMinute: hhmmToMinutes(r.end),
        });
      });
    });
    return result;
  };

  const handleSave = async () => {
    if (saving || !entityId) return;
    const validation = validateHours();
    if (validation) {
      setHoursError(validation);
      return;
    }
    setHoursError("");
    setSaving(true);
    try {
      await entityService.setBusinessHours(entityId, flattenHours());
      showToast("Horários de atendimento salvos com sucesso.", "success");
    } catch (error) {
      console.error("Erro ao salvar horários de atendimento:", error);
      showToast(
        "Erro ao salvar os horários de atendimento. Tente novamente.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <SavingOverlay open={saving} />
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box
            className="c4u-page-header c4u-page-header--in-panel"
          >
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                {entityName} / Horários de Atendimento
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Defina uma ou mais faixas por dia. Dia sem faixa é considerado
                fechado. As faixas delimitam a capacidade útil usada nos
                relatórios de ocupação.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
              <FaqButton />
            </Box>
          </Box>

          {/* Painel único: atalhos de cópia, grade dos dias, erro e Salvar moram juntos */}
          <div className="c4u-panel">
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mb: 1.5,
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={copyMondayToWeekdays}
              disabled={loading}
              sx={{
                textTransform: "none",
                color: colors.primary,
                borderColor: colors.border,
                flex: { xs: "1 1 100%", sm: "0 0 auto" },
              }}
            >
              Copiar 2ª → ter a sex
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={copyMondayToAll}
              disabled={loading}
              sx={{
                textTransform: "none",
                color: colors.primary,
                borderColor: colors.border,
                flex: { xs: "1 1 100%", sm: "0 0 auto" },
              }}
            >
              Copiar 2ª → todos
            </Button>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "1rem 0",
              }}
            >
              <CircularProgress size={18} sx={{ color: colors.primary }} />
              <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>
                Carregando horários...
              </Typography>
            </Box>
          ) : (
            <Box className="admin-plans-list-container"
            sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
              <Box className="admin-plans-table admin-entities-table">
                <Box
                  className="c4u-table__header">
                  <Box
                    className="admin-plans-header-cell"
                    sx={{ flex: "0 0 160px", textAlign: "left" }}
                  >
                    Dia da semana
                  </Box>
                  <Box
                    className="admin-plans-header-cell"
                    sx={{ flex: "1 1 auto", minWidth: 200, textAlign: "left" }}
                  >
                    Faixas de atendimento
                  </Box>
                  <Box
                    className="admin-plans-header-cell"
                    sx={{ flex: "0 0 90px", justifyContent: "flex-end" }}
                  >
                    Ações
                  </Box>
                </Box>

                <Box className="admin-plans-table-body">
                  {WEEKDAY_LABELS.map((label, weekday) => {
                    const ranges = dayRanges[weekday];
                    return (
                      <Box
                        key={weekday}
                        className="c4u-table__row"
                        sx={{
                          alignItems: "flex-start",
                        }}
                      >
                        <Box
                          className="admin-plans-cell"
                          sx={{
                            flex: "0 0 160px",
                            textAlign: "left",
                            fontWeight: 600,
                            pt: "0.5rem",
                          }}
                          data-label="Dia da semana"
                        >
                          {label}
                        </Box>
                        <Box
                          className="admin-plans-cell"
                          sx={{
                            flex: "1 1 auto",
                            minWidth: 200,
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.5rem",
                          }}
                          data-label="Faixas de atendimento"
                        >
                          {ranges.length === 0 ? (
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: colors.textSecondary,
                                fontStyle: "italic",
                                pt: "0.5rem",
                              }}
                            >
                              Fechado
                            </Typography>
                          ) : (
                            ranges.map((range, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "0.5rem",
                                }}
                              >
                                <TextField
                                  select
                                  value={range.start}
                                  onChange={(e) =>
                                    updateRange(weekday, index, "start", e.target.value)
                                  }
                                  inputProps={{
                                    "aria-label": `${label} início da faixa ${index + 1}`,
                                  }}
                                  SelectProps={{
                                    MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
                                  }}
                                  sx={{
                                    width: 120,
                                    "& .MuiOutlinedInput-root": {
                                      height: inputs.default.height,
                                      "& fieldset": { borderColor: colors.border },
                                      "&:hover fieldset": { borderColor: colors.border },
                                      "&.Mui-focused fieldset": {
                                        borderColor: colors.primary,
                                      },
                                    },
                                  }}
                                >
                                  {timeOptions(range.start).map((t) => (
                                    <MenuItem key={t} value={t}>
                                      {t}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <Typography
                                  sx={{ fontSize: "0.85rem", color: colors.textSecondary }}
                                >
                                  às
                                </Typography>
                                <TextField
                                  select
                                  value={range.end}
                                  onChange={(e) =>
                                    updateRange(weekday, index, "end", e.target.value)
                                  }
                                  inputProps={{
                                    "aria-label": `${label} fim da faixa ${index + 1}`,
                                  }}
                                  SelectProps={{
                                    MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
                                  }}
                                  sx={{
                                    width: 120,
                                    "& .MuiOutlinedInput-root": {
                                      height: inputs.default.height,
                                      "& fieldset": { borderColor: colors.border },
                                      "&:hover fieldset": { borderColor: colors.border },
                                      "&.Mui-focused fieldset": {
                                        borderColor: colors.primary,
                                      },
                                    },
                                  }}
                                >
                                  {timeOptions(range.end).map((t) => (
                                    <MenuItem key={t} value={t}>
                                      {t}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <IconButton
                                  onClick={() => removeRange(weekday, index)}
                                  aria-label={`Remover faixa ${index + 1} de ${label}`}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    color: "#dc3545",
                                    "&:hover": { backgroundColor: "rgba(220,53,69,0.08)" },
                                  }}
                                >
                                  <Close sx={{ fontSize: "1.1rem" }} />
                                </IconButton>
                              </Box>
                            ))
                          )}
                        </Box>
                        <Box
                          className="admin-plans-cell admin-plans-actions"
                          sx={{ flex: "0 0 90px", textAlign: "right", pt: "0.25rem" }}
                          data-label="Ações"
                        >
                          <Tooltip title="Adicionar faixa">
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={() => addRange(weekday)}
                              sx={{
                                backgroundColor: "#28a745",
                                color: "white",
                                "&:hover": { backgroundColor: "#218838" },
                              }}
                            >
                              <Add sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          {hoursError && (
            <Typography
              sx={{ fontSize: "0.8rem", color: "#dc3545", marginTop: "0.5rem" }}
            >
              {hoursError}
            </Typography>
          )}

          <Box sx={{ mt: 2, display: "flex", gap: "1rem" }}>
            <Button
              onClick={handleSave}
              disabled={saving || loading}
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={16} sx={{ color: colors.white }} />
                ) : undefined
              }
              sx={{
                textTransform: "none",
                backgroundColor: colors.primary,
                color: colors.white,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#029AAB", boxShadow: "none" },
                "&.Mui-disabled": {
                  backgroundColor: colors.primary,
                  opacity: 0.7,
                  color: colors.white,
                },
              }}
            >
              {saving ? "Processando..." : "Salvar"}
            </Button>
          </Box>
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default EntityBusinessHours;
